import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Autocomplete,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Chip,
  TablePagination,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CategoryIcon from '@mui/icons-material/Category';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LotPicker from '../inventory/lots/LotPicker';
import GuideButton from '../../common/guide/GuideButton';
import { emptyLotSelection, type LotSelection } from '../inventory/types/lots.types';
import { resolveBarcodes } from '../inventory/services/packagingService';

// ─── Tipler ──────────────────────────────────────────────────────────────────

interface Warehouse {
  id: string;
  name: string;
  address?: string;
  description?: string;
  isDefault?: boolean;
  branchesId?: string | null;
  warehouseType?: string;
}

interface InventoryItem {
  id: string;
  name: string;
  barcode?: string;
  categoryId?: string | null;
  stockCode?: string;
  quantity: number; // Seçili depodaki miktar (WarehouseStock'tan)
  unit?: string;
  trackLots?: boolean;
}

interface TransferItem {
  sourceItem: InventoryItem;
  quantity: number;
  lotSelection: LotSelection;
}

interface LocationAssignment {
  productId: string;
  itemName: string;
  locationId: string;
  locationCode: string;
  locationName: string;
  reason: string;
}

interface WarehouseTransferModalProps {
  open: boolean;
  onClose: () => void;
  warehouses: Warehouse[];
  companyId: string | null;
}

// ─── Sabitler ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

// ─── Yardımcı: warehouseStocks[]'dan seçili depo miktarını çek ──────────────

function extractQuantity(data: any, warehouseId: string): number {
  const stocks: any[] = data.warehouseStocks ?? data.WarehouseStocks ?? [];
  return stocks
    .filter(
      (ws: any) =>
        (ws.warehouseId ?? ws.WarehouseId)?.toLowerCase() ===
        warehouseId.toLowerCase()
    )
    .reduce((sum: number, ws: any) => sum + Number(ws.quantity ?? ws.Quantity ?? 0), 0);
}

function mapItem(data: any, warehouseId: string): InventoryItem {
  return {
    id: data.id ?? data.Id ?? '',
    name: data.name ?? data.Name ?? '',
    barcode: data.barcode ?? data.Barcode ?? '',
    categoryId: data.categoryId ?? data.CategoryId ?? null,
    stockCode: data.stockCode ?? data.StockCode ?? '',
    quantity: extractQuantity(data, warehouseId),
    unit: data.unit ?? data.Unit ?? 'adet',
    trackLots: (data.trackLots ?? data.TrackLots) === true,
  };
}

// ─── Bileşen ──────────────────────────────────────────────────────────────────

const WarehouseTransferModal: React.FC<WarehouseTransferModalProps> = ({
  open,
  onClose,
  warehouses,
  companyId,
}) => {
  const { categories, getCategoryName, isModuleEnabled } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // Depo seçimi
  const [sourceWarehouse, setSourceWarehouse] = useState<Warehouse | null>(null);
  const [targetWarehouse, setTargetWarehouse] = useState<Warehouse | null>(null);

  // Kaynak depo ürünleri (geçerli sayfa)
  const [sourceItems, setSourceItems] = useState<InventoryItem[]>([]);
  const [totalSourceCount, setTotalSourceCount] = useState(0);
  const [sourcePage, setSourcePage] = useState(0); // 0-tabanlı (TablePagination için)
  const [sourceLoading, setSourceLoading] = useState(false);

  // Filtreler
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  /**
   * Bekleyen miktarlar — ürün ID'si → girilen miktar
   * Sayfa değişiminde, kategori/arama filtresi değişiminde korunur.
   * Modal kapatılana kadar silinmez.
   */
  const [pendingQuantities, setPendingQuantities] = useState<Record<string, number | ''>>({});
  // Arama kutusunda barkod okutulup Enter: ürün listede bulununca bekleyen miktara eklenecek adet
  // (koli barkodunda içerik kadar, ör. 24).
  const [pendingScan, setPendingScan] = useState<{ itemId: string; add: number; label: string; code: string } | null>(null);
  // Son başarılı yüklemenin arama metni: okutulan barkodun sonuçları gelmeden karar verilmesin.
  const [lastLoadedSearch, setLastLoadedSearch] = useState<string | null>(null);
  const [scanMessage, setScanMessage] = useState<string | null>(null);

  // Transfer listesi
  const [transferItems, setTransferItems] = useState<TransferItem[]>([]);

  // Durum
  const [transferLoading, setTransferLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [assignmentResult, setAssignmentResult] = useState<LocationAssignment[]>([]);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  // Race-condition koruması
  const fetchRef = useRef(0);

  // ─── Arama debounce ─────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchTerm),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ─── Kaynak depo ürünlerini yükle (server-side) ─────────────────────────────
  const loadSourceItems = useCallback(
    async (warehouseId: string, page: number, search: string, categoryId: string | null) => {
      if (!companyId) return;

      const currentFetch = ++fetchRef.current;
      setSourceLoading(true);
      setError(null);

      try {
        const response = await accountingApi.post(
          ACCOUNTING_API.INVENTORY_ITEMS_PAGED,
          {
            PageNumber: page + 1,        // API 1-tabanlı
            PageSize: PAGE_SIZE,
            WarehouseId: warehouseId,
            SearchTerm: search || null,
            CategoryId: categoryId || null,
            IsActive: true,
            HideZeroStock: true,          // Sadece stoğu olan ürünler
          }
        );

        if (currentFetch !== fetchRef.current) return;

        const raw: any[] = response.data?.items ?? response.data?.Items ?? [];
        const total: number = response.data?.totalCount ?? response.data?.TotalCount ?? 0;

        setSourceItems(raw.map((d) => mapItem(d, warehouseId)));
        setTotalSourceCount(total);
        setLastLoadedSearch(search || '');
      } catch (err) {
        if (currentFetch !== fetchRef.current) return;
        console.error('[WarehouseTransfer] Ürünler yüklenirken hata:', err);
        setError(tt('Kaynak depo ürünleri yüklenirken bir hata oluştu.', 'An error occurred while loading source warehouse products.'));
      } finally {
        if (currentFetch === fetchRef.current) setSourceLoading(false);
      }
    },
    [companyId]
  );

  // Kaynak depo veya filtreler değişince: sayfayı sıfırla ve yükle
  useEffect(() => {
    setSourcePage(0);
    setSourceItems([]);
    setTotalSourceCount(0);

    if (sourceWarehouse && companyId) {
      loadSourceItems(sourceWarehouse.id, 0, debouncedSearch, selectedCategoryId);
    }
  }, [sourceWarehouse, companyId, debouncedSearch, selectedCategoryId]);

  // Sayfa değişince yükle
  useEffect(() => {
    if (sourceWarehouse && companyId) {
      loadSourceItems(sourceWarehouse.id, sourcePage, debouncedSearch, selectedCategoryId);
    }
  }, [sourcePage]);

  // ─── Modal kapatma ──────────────────────────────────────────────────────────
  const handleClose = () => {
    setSourceWarehouse(null);
    setTargetWarehouse(null);
    setSourceItems([]);
    setTotalSourceCount(0);
    setSourcePage(0);
    setTransferItems([]);
    setPendingQuantities({});
    setSearchTerm('');
    setDebouncedSearch('');
    setSelectedCategoryId(null);
    setError(null);
    setSuccessMessage(null);
    setAssignmentResult([]);
    setResultDialogOpen(false);
    onClose();
  };

  const isTargetSmart = targetWarehouse?.warehouseType === 'Smart';

  // ─── Barkod okutma (arama kutusunda Enter) ───────────────────────────────────
  const handleSearchEnter = async () => {
    const code = searchTerm.trim();
    if (!code || !sourceWarehouse) return;
    setScanMessage(null);
    try {
      const [resolved] = await resolveBarcodes([code]);
      const match = resolved?.matches[0];
      if (!match) return; // barkod değil: normal arama sonuçları kalır
      const add = match.isPackage && match.factor > 0 ? match.factor : 1;
      setPendingScan({
        itemId: match.itemId.toLowerCase(),
        add,
        label: match.isPackage && match.unitName ? `1 ${match.unitName} (${add})` : '1',
        code,
      });
      setDebouncedSearch(code); // listeyi beklemeden yenile
    } catch {
      // ağ hatası: arama sonuçlarıyla devam
    }
  };

  // Okutulan ürün listeye gelince bekleyen miktara eklenir
  useEffect(() => {
    if (!pendingScan || sourceLoading || lastLoadedSearch !== pendingScan.code) return;
    const item = sourceItems.find((s) => s.id.toLowerCase() === pendingScan.itemId);
    if (!item) {
      setScanMessage(tt('Okutulan ürün bu depoda stokta yok.', 'Scanned product has no stock in this warehouse.'));
      setPendingScan(null);
      return;
    }
    setPendingQuantities((prev) => ({ ...prev, [item.id]: (Number(prev[item.id]) || 0) + pendingScan.add }));
    setScanMessage(`${item.name}: +${pendingScan.label}`);
    setSearchTerm('');
    setPendingScan(null);
  }, [pendingScan, sourceItems, sourceLoading, lastLoadedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Bekleyen miktar güncelleme ─────────────────────────────────────────────
  const handlePendingChange = (itemId: string, value: string) => {
    if (value === '') {
      setPendingQuantities((prev) => ({ ...prev, [itemId]: '' }));
      return;
    }
    const num = Math.max(0, Number(value));
    setPendingQuantities((prev) => ({ ...prev, [itemId]: num }));
  };

  // ─── "Aktar" butonu: bekleyen miktarları transfer listesine al ──────────────
  const handleTransferPending = () => {
    if (!sourceWarehouse || !targetWarehouse) {
      setError(tt('Lütfen önce kaynak ve hedef depo seçin.', 'Please select source and target warehouse first.'));
      return;
    }

    const toAdd: TransferItem[] = [];
    const updatedTransfer = [...transferItems];
    const clearedIds: string[] = [];

    sourceItems.forEach((item) => {
      const qty = Number(pendingQuantities[item.id] ?? 0);
      if (qty <= 0) return;
      if (qty > item.quantity) {
        setError(`"${item.name}" ${tt('için girilen miktar mevcut stoktan fazla.', 'entered quantity exceeds current stock.')}`);
        return;
      }

      const existing = updatedTransfer.findIndex((t) => t.sourceItem.id === item.id);
      if (existing !== -1) {
        updatedTransfer[existing] = { ...updatedTransfer[existing], quantity: qty };
      } else {
        toAdd.push({ sourceItem: item, quantity: qty, lotSelection: emptyLotSelection() });
      }
      clearedIds.push(item.id);
    });

    const finalList = [...updatedTransfer, ...toAdd];
    if (finalList.length === transferItems.length && toAdd.length === 0 && clearedIds.length === 0) {
      setError(tt('Aktarılacak miktar girilmedi.', 'No transfer quantity entered.'));
      return;
    }

    setTransferItems(finalList);

    // Aktarılan ürünlerin pending miktarlarını temizle
    setPendingQuantities((prev) => {
      const next = { ...prev };
      clearedIds.forEach((id) => delete next[id]);
      return next;
    });

    setSuccessMessage(`${toAdd.length > 0 ? toAdd.length : clearedIds.length} ${tt('ürün transfer listesine eklendi / güncellendi.', 'items added / updated in transfer list.')}`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // ─── "Tümünü Aktar": geçerli sayfadaki tüm ürünleri max stokla ekle ─────────
  const handleTransferAll = () => {
    if (!sourceWarehouse || !targetWarehouse) {
      setError(tt('Lütfen önce kaynak ve hedef depo seçin.', 'Please select source and target warehouse first.'));
      return;
    }
    if (sourceItems.length === 0) {
      setError(tt('Kaynak depoda aktarılacak ürün bulunmuyor.', 'No transferable item found in source warehouse.'));
      return;
    }

    const updatedTransfer = [...transferItems];
    let added = 0;

    sourceItems.forEach((item) => {
      if (item.quantity <= 0) return;
      const existing = updatedTransfer.findIndex((t) => t.sourceItem.id === item.id);
      if (existing !== -1) {
        updatedTransfer[existing] = { ...updatedTransfer[existing], quantity: item.quantity };
      } else {
        updatedTransfer.push({ sourceItem: item, quantity: item.quantity, lotSelection: emptyLotSelection() });
        added++;
      }
    });

    setTransferItems(updatedTransfer);
    setSuccessMessage(`${added} ${tt('ürün transfer listesine eklendi.', 'items added to transfer list.')}`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // ─── Transfer listesinden kaldır ────────────────────────────────────────────
  const handleRemoveFromTransferList = (index: number) => {
    setTransferItems((prev) => prev.filter((_, i) => i !== index));
  };

  // ─── Transfer kaydet ────────────────────────────────────────────────────────
  const handleSaveTransfer = async () => {
    setConfirmDialogOpen(false);
    if (!sourceWarehouse || !targetWarehouse || !companyId || transferItems.length === 0) return;

    // Elle lot seçimi transfer miktarına eşit olmalı; sunucu da aynı kalemi reddeder.
    const lotMismatch = transferItems.filter((item) => {
      if (item.lotSelection?.mode !== 'manual') return false;
      const lots = item.lotSelection.lots.filter((lot) => lot.lotId);
      if (lots.length === 0) return false;
      const total = lots.reduce((sum, lot) => sum + (Number(lot.quantity) || 0), 0);
      return Math.abs(total - item.quantity) > 0.0001;
    });
    if (lotMismatch.length > 0) {
      setError(tt(
        `Lot miktarları transfer miktarına eşit olmalı: ${lotMismatch.map((item) => item.sourceItem.name).join(', ')}`,
        `Lot quantities must equal the transfer quantity: ${lotMismatch.map((item) => item.sourceItem.name).join(', ')}`,
      ));
      return;
    }

    try {
      setTransferLoading(true);
      setError(null);

      const transferRequest = {
        CompanyId: companyId,
        SourceWarehouseId: sourceWarehouse.id,
        TargetWarehouseId: targetWarehouse.id,
        Items: transferItems.map((item) => ({
          SourceItemId: item.sourceItem.id,
          ItemName: item.sourceItem.name,
          StockCode: item.sourceItem.stockCode || null,
          Quantity: item.quantity,
          Unit: item.sourceItem.unit || 'Adet',
          Lots: item.lotSelection?.mode === 'manual'
            ? item.lotSelection.lots.filter((lot) => lot.lotId).map((lot) => ({ LotId: lot.lotId, Quantity: lot.quantity }))
            : undefined,
        })),
        Notes: null,
        LocationAssignmentMode: isTargetSmart ? 'Random' : 'None',
      };

      const response = await accountingApi.post(
        ACCOUNTING_API.INVENTORY_WAREHOUSE_TRANSFER_CREATE,
        transferRequest
      );

      const assignmentsRaw: any[] =
        response.data?.locationAssignments ?? response.data?.LocationAssignments ?? [];
      const assignments: LocationAssignment[] = assignmentsRaw.map((a) => ({
        productId: a.productId ?? a.ProductId ?? '',
        itemName: a.itemName ?? a.ItemName ?? '',
        locationId: a.locationId ?? a.LocationId ?? '',
        locationCode: a.locationCode ?? a.LocationCode ?? '',
        locationName: a.locationName ?? a.LocationName ?? '',
        reason: a.reason ?? a.Reason ?? '',
      }));

      setAssignmentResult(assignments);
      setSuccessMessage(response.data?.message || tt('Transfer başarıyla tamamlandı.', 'Transfer completed successfully.'));
      setTransferItems([]);
      setPendingQuantities({});
      if (assignments.length > 0) {
        setResultDialogOpen(true);
      }

      // Kaynak depo ürünlerini yenile
      if (sourceWarehouse) {
        loadSourceItems(sourceWarehouse.id, sourcePage, debouncedSearch, selectedCategoryId);
      }
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.errors?.[0] ||
        tt('Transfer sırasında bir hata oluştu.', 'An error occurred during transfer.');
      setError(msg);
    } finally {
      setTransferLoading(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  const autocompleteRenderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Warehouse
  ) => (
    <li {...props} key={option.id}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarehouseIcon
          fontSize="small"
          sx={{ color: option.isDefault ? 'primary.main' : 'action.active' }}
        />
        <Typography variant="body2">
          {option.name}
          {option.isDefault && ` (${tt('Varsayılan', 'Default')})`}
        </Typography>
        <Chip
          size="small"
          label={option.warehouseType === 'Smart' ? tt('Akıllı', 'Smart') : tt('Normal', 'Normal')}
          color={option.warehouseType === 'Smart' ? 'info' : 'default'}
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      </Box>
    </li>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, minHeight: '80vh' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SwapHorizIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="span">
              {tt('Depolar Arası Stok Transfer', 'Stock Transfer Between Warehouses')}
            </Typography>
            <GuideButton guideKey="inventory.warehouseTransfer" size="small" sx={{ ml: 0.5 }} />
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* Depo Seçimi */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={5}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                {tt('Kaynak Depo', 'Source Warehouse')}
              </Typography>
              <Autocomplete
                size="small"
                options={warehouses}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={sourceWarehouse}
                onChange={(_, newValue) => {
                  if (newValue && newValue.id === targetWarehouse?.id) {
                    setError(tt('Kaynak ve hedef depo aynı olamaz.', 'Source and target warehouse cannot be the same.'));
                    return;
                  }
                  setSourceWarehouse(newValue);
                  setTransferItems([]);
                  setPendingQuantities({});
                  setError(null);
                }}
                renderOption={autocompleteRenderOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={tt('Kaynak Depo Seçin', 'Select Source Warehouse')}
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <WarehouseIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={2}
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <ArrowRightAltIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Grid>

            <Grid item xs={5}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                {tt('Hedef Depo', 'Target Warehouse')}
              </Typography>
              <Autocomplete
                size="small"
                options={warehouses}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={targetWarehouse}
                onChange={(_, newValue) => {
                  if (newValue && newValue.id === sourceWarehouse?.id) {
                    setError(tt('Kaynak ve hedef depo aynı olamaz.', 'Source and target warehouse cannot be the same.'));
                    return;
                  }
                  setTargetWarehouse(newValue);
                  setTransferItems([]);
                  setPendingQuantities({});
                  setError(null);
                }}
                renderOption={autocompleteRenderOption}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={tt('Hedef Depo Seçin', 'Select Target Warehouse')}
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <WarehouseIcon fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Ana İçerik */}
          <Grid container spacing={2}>
            {/* Sol: Kaynak Depo Ürünleri */}
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {tt('Kaynak Depo Ürünleri', 'Source Warehouse Products')}
                {sourceWarehouse && (
                  <Chip
                    label={`${sourceWarehouse.name} (${sourceWarehouse.warehouseType === 'Smart' ? tt('Akıllı', 'Smart') : tt('Normal', 'Normal')})`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>

              {/* Filtre satırı: ara (dar) + kategori */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField
                  size="small"
                  placeholder={tt('Ara veya barkod okut...', 'Search or scan...')}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setScanMessage(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchEnter();
                    }
                  }}
                  helperText={scanMessage || undefined}
                  disabled={!sourceWarehouse}
                  sx={{ width: '45%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Autocomplete
                  size="small"
                  options={categories}
                  getOptionLabel={(c) => c.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={categories.find((c) => c.id === selectedCategoryId) ?? null}
                  onChange={(_, newVal) => setSelectedCategoryId(newVal?.id ?? null)}
                  disabled={!sourceWarehouse}
                  sx={{ width: '55%' }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={tt('Kategori', 'Category')}
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <CategoryIcon fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {sourceLoading && sourceItems.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : !sourceWarehouse ? (
                <EmptyBox icon={<WarehouseIcon />} text={tt('Lütfen önce kaynak depo seçin', 'Please select source warehouse first')} />
              ) : sourceItems.length === 0 ? (
                <EmptyBox
                  icon={<InventoryIcon />}
                  text={
                    searchTerm || selectedCategoryId
                      ? tt('Filtreye uygun ürün bulunamadı', 'No item found for selected filter')
                      : tt('Bu depoda stoklu ürün bulunmuyor', 'No in-stock item in this warehouse')
                  }
                />
              ) : (
                <Paper sx={{ maxHeight: 420, overflow: 'auto', position: 'relative' }}>
                  {sourceLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ color: 'white', bgcolor: 'primary.main', fontWeight: 'bold' }}
                          >
                            {tt('Ürün Adı', 'Item Name')}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: 'white',
                              bgcolor: 'primary.main',
                              fontWeight: 'bold',
                              width: 90,
                            }}
                          >
                            {tt('Stok', 'Stock')}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: 'white',
                              bgcolor: 'primary.main',
                              fontWeight: 'bold',
                              width: 110,
                            }}
                          >
                            {tt('To Transfer', 'To Transfer')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sourceItems.map((item) => {
                          const pending = pendingQuantities[item.id] ?? '';
                          const pendingNum = pending === '' ? 0 : Number(pending);
                          const isOverflow = pendingNum > item.quantity;
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <InventoryIcon
                                    fontSize="small"
                                    sx={{ mr: 0.5, color: 'primary.main', flexShrink: 0 }}
                                  />
                                  <Box>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                      {item.name}
                                    </Typography>
                                    {item.stockCode && (
                                      <Typography variant="caption" color="text.secondary">
                                        {item.stockCode}
                                      </Typography>
                                    )}
                                    {item.categoryId && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {getCategoryName(item.categoryId)}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {item.quantity}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.unit || tt('adet', 'piece')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={pending}
                                  onChange={(e) =>
                                    handlePendingChange(item.id, e.target.value)
                                  }
                                  error={isOverflow}
                                  title={
                                    isOverflow
                                      ? `${tt('Maksimum', 'Maximum')} ${item.quantity} ${item.unit || tt('adet', 'piece')}`
                                      : ''
                                  }
                                  inputProps={{ min: 0, max: item.quantity, step: 1 }}
                                  sx={{ width: 90 }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={totalSourceCount}
                    page={sourcePage}
                    rowsPerPage={PAGE_SIZE}
                    rowsPerPageOptions={[PAGE_SIZE]}
                    onPageChange={(_, newPage) => setSourcePage(newPage)}
                    labelDisplayedRows={({ from, to, count }) =>
                      (isTr ? `${count} kayıttan ${from}-${to}` : `${from}-${to} of ${count}`)
                    }
                  />
                </Paper>
              )}
            </Grid>

            {/* Sağ: Transfer Listesi */}
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {tt('Transfer Edilecek Ürünler', 'Products to Transfer')}
                {targetWarehouse && (
                  <Chip
                    label={`${targetWarehouse.name} (${targetWarehouse.warehouseType === 'Smart' ? tt('Akıllı', 'Smart') : tt('Normal', 'Normal')})`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>

              {transferItems.length === 0 ? (
                <EmptyBox
                  icon={<SwapHorizIcon />}
                  text={tt('Henüz ürün eklenmedi.\nSol tarafta miktar girin, ardından "Aktar" butonuna basın.', 'No item added yet.\nEnter quantity on left side, then click "Transfer".')}
                  height={250}
                />
              ) : (
                <>
                  <Paper sx={{ maxHeight: 380, overflow: 'auto' }}>
                    <TableContainer>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                color: 'white',
                                bgcolor: 'secondary.main',
                                fontWeight: 'bold',
                              }}
                            >
                              {tt('Ürün Adı', 'Item Name')}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: 'white',
                                bgcolor: 'secondary.main',
                                fontWeight: 'bold',
                                width: 90,
                              }}
                            >
                              {tt('Miktar', 'Quantity')}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: 'white',
                                bgcolor: 'secondary.main',
                                fontWeight: 'bold',
                                width: 50,
                              }}
                            />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transferItems.map((item, index) => (
                            <TableRow key={item.sourceItem.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <InventoryIcon
                                    fontSize="small"
                                    sx={{ mr: 0.5, color: 'secondary.main', flexShrink: 0 }}
                                  />
                                  <Box>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                      {item.sourceItem.name}
                                    </Typography>
                                    {item.sourceItem.stockCode && (
                                      <Typography variant="caption" color="text.secondary">
                                        {item.sourceItem.stockCode}
                                      </Typography>
                                    )}
                                    {isModuleEnabled('lot_tracking')
                                      && item.sourceItem.trackLots
                                      && sourceWarehouse
                                      && targetWarehouse
                                      && sourceWarehouse.warehouseType !== 'Smart'
                                      && targetWarehouse.warehouseType !== 'Smart' && (
                                      <LotPicker
                                        itemId={item.sourceItem.id}
                                        warehouseId={sourceWarehouse.id}
                                        quantity={item.quantity}
                                        direction="OUT"
                                        allowMultiple
                                        value={item.lotSelection}
                                        onChange={(lotSelection) => {
                                          setTransferItems((prev) => prev.map((row, rowIndex) => (
                                            rowIndex === index ? { ...row, lotSelection } : row
                                          )));
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {item.quantity}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.sourceItem.unit || tt('adet', 'piece')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveFromTransferList(index)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (!sourceWarehouse || !targetWarehouse) {
                          setError(tt('Lütfen kaynak ve hedef depo seçin.', 'Please select source and target warehouse.'));
                          return;
                        }
                        setConfirmDialogOpen(true);
                      }}
                      startIcon={<SaveIcon />}
                      disabled={transferItems.length === 0 || transferLoading}
                    >
                      {tt('Transferi Kaydet', 'Save Transfer')}
                    </Button>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.12)', gap: 1 }}>
          {/* Tümünü Aktar */}
          <Button
            onClick={handleTransferAll}
            color="primary"
            variant="outlined"
            disabled={!sourceWarehouse || !targetWarehouse || sourceItems.length === 0}
            startIcon={<SwapHorizIcon />}
          >
            {tt('Tümünü Aktar', 'Transfer All')}
          </Button>

          {/* Aktar (girilen miktarları transfer listesine al) */}
          <Button
            onClick={handleTransferPending}
            color="success"
            variant="contained"
            disabled={!sourceWarehouse || !targetWarehouse}
            startIcon={<SendIcon />}
          >
            {tt('Aktar', 'Transfer')}
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button onClick={handleClose} color="inherit">
            {tt('Kapat', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Onay Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{tt('Stok Transfer Onayı', 'Stock Transfer Confirmation')}</DialogTitle>
        <DialogContent>
          {sourceWarehouse && targetWarehouse && (
            <>
              <Typography variant="body1" gutterBottom>
                {tt(`"${sourceWarehouse.name}" → "${targetWarehouse.name}" transferi onaylıyor musunuz?`, `Do you confirm transfer "${sourceWarehouse.name}" → "${targetWarehouse.name}"?`)}
              </Typography>
              {isTargetSmart && (
                <Alert severity="info" sx={{ mt: 1 }} icon={<LocationOnIcon />}>
                  {tt(
                    'Lokasyonlar otomatik ve rastgele atanacak.',
                    'Locations will be assigned automatically at random.'
                  )}
                </Alert>
              )}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  maxHeight: 250,
                  overflow: 'auto',
                }}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>{tt('Toplam Ürün:', 'Total Items:')}</strong> {transferItems.length} {tt('adet', 'pcs')}
                </Typography>
                {(transferItems.length <= 6 ? transferItems : transferItems.slice(0, 5)).map(
                  (item) => (
                    <Typography key={item.sourceItem.id} variant="body2">
                      • {item.sourceItem.name}: {item.quantity} {item.sourceItem.unit || tt('adet', 'piece')}
                    </Typography>
                  )
                )}
                {transferItems.length > 6 && (
                  <Typography variant="body2" color="text.secondary">
                    • ... {tt('ve', 'and')} {transferItems.length - 5} {tt('ürün daha', 'more items')}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
            {tt('İptal', 'Cancel')}
          </Button>
          <Button
            onClick={handleSaveTransfer}
            color="primary"
            variant="contained"
            startIcon={
              transferLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
            }
            disabled={transferLoading}
          >
            {transferLoading ? tt('Kaydediliyor...', 'Saving...') : tt('Transferi Onayla', 'Confirm Transfer')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resultDialogOpen}
        onClose={() => setResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOnIcon color="primary" />
          {tt('Atanan lokasyonlar', 'Assigned locations')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {tt(
              'Stok akıllı depo raflarına yazıldı. WMS panelinde / lokasyon stoğunda görünür.',
              'Stock was written to smart warehouse racks. It is visible on the WMS panel / location stock.'
            )}
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{tt('Ürün', 'Product')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{tt('Lokasyon', 'Location')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{tt('Atama', 'Assignment')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignmentResult.map((row) => (
                  <TableRow key={`${row.productId}-${row.locationId}`}>
                    <TableCell>
                      <Typography variant="body2">{row.itemName || row.productId}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {row.locationCode}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.locationName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{row.reason}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultDialogOpen(false)} variant="contained">
            {tt('Tamam', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Küçük yardımcı bileşen ─────────────────────────────────────────────────

function EmptyBox({
  icon,
  text,
  height = 160,
}: {
  icon: React.ReactNode;
  text: string;
  height?: number;
}) {
  return (
    <Box
      sx={{
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed grey',
        borderRadius: 1,
        bgcolor: 'action.hover',
        color: 'text.secondary',
        gap: 1,
        p: 2,
      }}
    >
      {icon}
      <Typography variant="body2" align="center" sx={{ whiteSpace: 'pre-line' }}>
        {text}
      </Typography>
    </Box>
  );
}

export default WarehouseTransferModal;
